import { useEffect, useState } from "react";
import { kpisAPI, mentionsAPI, visitorsAPI, statsAPI } from "../../lib/api";
import { BarChart3, TrendingUp, TrendingDown, Eye, MessageCircle, Youtube, Music, Search, Bell, Minus } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const FONTE_META = {
    youtube:       { label: "YouTube",        icon: Youtube,        color: "text-red-400",    bg: "bg-red-900/20" },
    spotify:       { label: "Spotify",        icon: Music,          color: "text-green-400",  bg: "bg-green-900/20" },
    google_trends: { label: "Google Trends",  icon: TrendingUp,     color: "text-blue-400",   bg: "bg-blue-900/20" },
    google_alerts: { label: "Google Alerts",  icon: Bell,           color: "text-yellow-400", bg: "bg-yellow-900/20" },
    brand24:       { label: "Brand24",        icon: MessageCircle,  color: "text-purple-400", bg: "bg-purple-900/20" },
    instagram:     { label: "Instagram",      icon: Eye,            color: "text-pink-400",   bg: "bg-pink-900/20" },
};

const SENTIMENTO_META = {
    positivo: { icon: TrendingUp,   color: "text-green-400", label: "Positivo" },
    neutro:   { icon: Minus,        color: "text-stone-400", label: "Neutro" },
    negativo: { icon: TrendingDown, color: "text-red-400",   label: "Negativo" },
};

function KpiCard({ metric, value, label, fonte, date }) {
    const meta = FONTE_META[fonte] || { label: fonte, icon: BarChart3, color: "text-stone-400", bg: "bg-stone-800" };
    const Icon = meta.icon;
    return (
        <div className={`bg-stone-900 border border-stone-800 rounded-xl p-4`}>
            <div className="flex items-center gap-2 mb-3">
                <div className={`p-1.5 rounded-lg ${meta.bg}`}>
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                </div>
                <span className="text-xs text-stone-400">{meta.label}</span>
                {date && <span className="text-xs text-stone-600 ml-auto">{date}</span>}
            </div>
            <p className="text-2xl font-bold text-stone-100">{Number(value).toLocaleString("pt-BR")}</p>
            <p className="text-xs text-stone-400 mt-0.5">{label || metric}</p>
        </div>
    );
}

export default function AnalyticsAdmin() {
    const [kpis, setKpis] = useState([]);
    const [mentionStats, setMentionStats] = useState(null);
    const [visitorStats, setVisitorStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);

    const load = async () => {
        setLoading(true);
        try {
            const [k, m, v] = await Promise.all([
                kpisAPI.latest(),
                mentionsAPI.stats(),
                visitorsAPI.stats(),
            ]);
            setKpis(k.data || []);
            setMentionStats(m.data);
            setVisitorStats(v.data);
        } catch { toast.error("Erro ao carregar analytics"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // Agrupar KPIs por fonte
    const kpisByFonte = {};
    kpis.forEach(k => {
        const fonte = k._id?.fonte;
        if (!kpisByFonte[fonte]) kpisByFonte[fonte] = [];
        kpisByFonte[fonte].push(k);
    });

    return (
        <>
        <Header title="KPIs & Analytics" subtitle="Dados coletados automaticamente via n8n" actions={
            <button onClick={load} className="text-xs text-stone-400 hover:text-stone-200 border border-stone-700 rounded-lg px-3 py-1.5 transition-colors">
                    Atualizar
                </button>
        } />
        <div className="p-6 space-y-6">

            {/* KPIs externos */}
            <div>
                <h2 className="text-sm font-semibold text-stone-400 mb-3">Plataformas externas</h2>
                {loading ? (
                    <div className="text-stone-500 text-sm py-8 text-center">Carregando...</div>
                ) : kpis.length === 0 ? (
                    <div className="bg-stone-900 border border-stone-800 rounded-xl p-8 text-center">
                        <BarChart3 className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                        <p className="text-stone-400 text-sm">Nenhum dado de KPI ainda.</p>
                        <p className="text-stone-500 text-xs mt-1">
                            Configure os flows do n8n para coletar YouTube, Spotify, Trends e menções.<br />
                            Os dados chegam via <code className="bg-stone-800 px-1 rounded">POST /api/webhook/kpis</code>
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {kpis.map((k, i) => (
                            <KpiCard key={i}
                                metric={k._id?.metric} fonte={k._id?.fonte}
                                value={k.value} label={k.label} date={k.date} />
                        ))}
                    </div>
                )}
            </div>

            {/* Visitantes do site */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                    <h2 className="text-sm font-semibold text-stone-200 mb-4 flex items-center gap-2">
                        <Eye className="w-4 h-4 text-sky-400" /> Visitantes do Site
                    </h2>
                    {visitorStats ? (
                        <div className="space-y-4">
                            <div>
                                <p className="text-2xl font-bold text-stone-100">{visitorStats.total?.toLocaleString("pt-BR")}</p>
                                <p className="text-xs text-stone-400">visitas totais</p>
                            </div>
                            <div>
                                <p className="text-xs text-stone-400 font-medium mb-2">Páginas mais visitadas</p>
                                <div className="space-y-1.5">
                                    {(visitorStats.by_page || []).slice(0, 5).map((p, i) => (
                                        <div key={i} className="flex items-center justify-between text-xs">
                                            <span className="text-stone-300 truncate max-w-[200px]">{p._id || "/"}</span>
                                            <span className="text-stone-400">{p.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-stone-400 font-medium mb-2">Por jornada</p>
                                <div className="space-y-1.5">
                                    {(visitorStats.by_segmento || []).map((s, i) => (
                                        <div key={i} className="flex items-center justify-between text-xs">
                                            <span className="text-stone-300">{s._id}</span>
                                            <span className="text-stone-400">{s.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-stone-500 text-xs">Nenhum dado ainda. Adicione o pixel de rastreamento ao site público.</p>
                    )}
                </div>

                {/* Menções */}
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                    <h2 className="text-sm font-semibold text-stone-200 mb-4 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-indigo-400" /> Menções & Sentimento
                    </h2>
                    {mentionStats ? (
                        <div className="space-y-4">
                            <div>
                                <p className="text-2xl font-bold text-stone-100">{mentionStats.total}</p>
                                <p className="text-xs text-stone-400">menções registradas</p>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                {["positivo", "neutro", "negativo"].map(s => {
                                    const meta = SENTIMENTO_META[s];
                                    const Icon = meta.icon;
                                    return (
                                        <div key={s} className="bg-stone-800 rounded-lg p-3 text-center">
                                            <Icon className={`w-4 h-4 ${meta.color} mx-auto mb-1`} />
                                            <p className="text-lg font-bold text-stone-100">{mentionStats[s]}</p>
                                            <p className="text-xs text-stone-500">{meta.label}</p>
                                        </div>
                                    );
                                })}
                            </div>
                            {mentionStats.total > 0 && (
                                <div className="space-y-1">
                                    <p className="text-xs text-stone-400 font-medium">Índice de sentimento</p>
                                    <div className="h-2 bg-stone-800 rounded-full overflow-hidden flex">
                                        <div className="h-full bg-green-600 rounded-l"
                                            style={{ width: `${(mentionStats.positivo / mentionStats.total) * 100}%` }} />
                                        <div className="h-full bg-stone-600"
                                            style={{ width: `${(mentionStats.neutro / mentionStats.total) * 100}%` }} />
                                        <div className="h-full bg-red-600 rounded-r"
                                            style={{ width: `${(mentionStats.negativo / mentionStats.total) * 100}%` }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-stone-500 text-xs">Configure o n8n para enviar menções via webhook.</p>
                    )}
                </div>
            </div>

            {/* Instruções de integração */}
            <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-stone-200 mb-3">Como conectar os KPIs</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-stone-400">
                    <div className="space-y-2">
                        <p className="font-medium text-stone-300">n8n → POST /api/webhook/kpis</p>
                        <pre className="bg-stone-800 rounded-lg p-3 text-stone-300 overflow-x-auto">{`[
  {
    "fonte": "youtube",
    "metric": "youtube_subscribers",
    "value": 12500,
    "label": "Inscritos YouTube",
    "date": "2026-09-02"
  }
]`}</pre>
                    </div>
                    <div className="space-y-2">
                        <p className="font-medium text-stone-300">n8n → POST /api/webhook/mentions</p>
                        <pre className="bg-stone-800 rounded-lg p-3 text-stone-300 overflow-x-auto">{`[
  {
    "query": "Tayane Larini",
    "titulo": "Título da menção",
    "fonte": "instagram",
    "url": "https://...",
    "sentimento": "positivo",
    "data": "2026-09-02"
  }
]`}</pre>
                    </div>
                </div>
                <p className="text-xs text-stone-500 mt-3">
                    Fontes suportadas: <code className="bg-stone-800 px-1 rounded">youtube · spotify · google_trends · google_alerts · brand24 · instagram</code>
                </p>
            </div>
        </div>
        </>
    );
}
